import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { useHistory, withRouter } from 'react-router-dom';
import { graphql } from 'react-apollo';
import compose from 'lodash.flowright';
import { connect } from 'react-redux';

import BlogIcon from '../../../../components/icons/BlogIcon';
import VideoIcon from '../../../../components/icons/VideoIcon';
import FavoritesIcon from '../../../../components/icons/FavoritesIcon';
import UploadIcon from '../../../../components/icons/UploadIcon';
import WebGalleryTitleBreadcrumb from './components/WebGalleryTitleBreadcrumb';
import {
  getWebGalleryBlogPath,
  getWebGalleryFavoritesPath,
  getWebGallerySlideshowPath,
  getWebGalleryVideosPath,
} from '../../utilities/web-galleries';
import {
  AlbumFolderWithBlogPosts,
  AlbumFolderWithVideos,
} from '../../graphql/queries/album-folder';
import {
  AlbumPhotoGroupWithBlogPosts,
  AlbumPhotoGroupWithVideos,
  AlbumWithBlogPosts,
  AlbumWithVideos,
} from '../../graphql/queries/album';
import { openModal as openModalAction } from '../../../../actions/modal-actions';
import {
  CONTRIBUTOR_SHARE_MODAL,
  PHONE_AUTH_MODAL,
} from '../../../../constants/modals';
import ContributeButton from '../../../../components/global/ContributeButton';
import {
  canAccessMyMatchesFromWebGallery,
  canUploadToChildAlbumFolders,
} from '../../../../util/product-permissions';
import MyMatchesButton from '../../../../components/global/MyMatchesButton';
import FeatureBlock from '../../../../components/contextual/FeatureBlock';
import { trackAuthEvent } from '../../utilities/analytics';
import { MeQuery } from '../../../../graphql/queries/account';
import { getBasenameFromEntity } from '../../../../util/role-definitions';
import {
  ALBUM_TYPE,
  ALBUM_SLIDESHOW_STATUS,
} from '../../../../constants/albums';
import { getAsset } from '../../../../util/assets';
import WebGalleryActionBarButton from './components/WebGalleryActionBarButton';
import HomeIcon from '../../../../components/icons/HomeIcon';
import ContributorShare from '../../../pro/components/ContributorShare/ContributorShare';
import InviteIcon from '../../../../components/icons/InviteIcon';
import WebGalleryActionDropdown from './components/WebGalleryActionDropdown';
import ContactCircleSaveModal from '../../../../components/global/ContactCircleSaveModal/modal';
import ContactCircleShareModal from '../../../../components/global/ContactCircleShareModal';
import { isVideoAlbum } from '../../../../util/album';
import { AlbumSlideshowForModeCheck } from '../../../../graphql/queries/albums';
import { isAlbumFolderOwner, isAlbumOwner } from '../../../../util/account';
import SlideshowIcon from '../../../../components/icons/SlideshowIcon';
import {
  AlbumFavoritePhotoIds,
  AlbumFolderFavoritePhotoIds,
} from '../../graphql/queries/favorites';

const propTypes = {
  albumFolder: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
  }),
  album: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
  }),
  albumPhotoGroup: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
  }),
  authenticated: PropTypes.bool,
  blogPostsTotal: PropTypes.number,
  unreadBlogPosts: PropTypes.number,
  videosTotal: PropTypes.number,
  login: PropTypes.func.isRequired,
  openModal: PropTypes.func,
  slideshow: PropTypes.shape({
    status: PropTypes.oneOf(Object.keys(ALBUM_SLIDESHOW_STATUS)),
  }),
  hasAlbumFavorites: PropTypes.bool,
  hasAlbumFolderFavorites: PropTypes.bool,
};

const WebGalleryActionBar = ({
  authenticated,
  albumFolder,
  album,
  albumPhotoGroup,
  blogPostsTotal,
  unreadBlogPosts,
  videosTotal,
  login,
  user,
  openModal,
  slideshow,
  hasAlbumFavorites,
  hasAlbumFolderFavorites,
}) => {
  const entity = albumPhotoGroup?.album || albumFolder || album;
  const history = useHistory();
  const blogUrl = getWebGalleryBlogPath(
    albumPhotoGroup || albumFolder || album,
  );
  const videosUrl = getWebGalleryVideosPath(
    albumPhotoGroup || albumFolder || album,
  );
  const showFavorites = !!hasAlbumFavorites || !!hasAlbumFolderFavorites;
  const favoritesUrl = getWebGalleryFavoritesPath(albumFolder, album);

  const isVideoAlbumBool = isVideoAlbum(album);

  const isOwner =
    isAlbumOwner(user, album) || isAlbumFolderOwner(user, albumFolder);

  const slideshowUrl = getWebGallerySlideshowPath(album, slideshow, isOwner);

  const canAccessBack =
    isOwner || (!authenticated && albumFolder?.album_type === ALBUM_TYPE.EVENT);

  const onFavoritesClick = event => {
    if (!authenticated) {
      event.preventDefault();

      login({
        introText: (
          <Fragment>
            Enter your mobile phone below to create and save your favorite
            photos!
          </Fragment>
        ),
        submitText: 'Send PIN Code',
        onLogin: () => {
          trackAuthEvent();
          history.push(favoritesUrl);
        },
      });
    } else {
      history.push(favoritesUrl);
    }
  };

  const backBtnLabel = (() => {
    const eventTypes = [ALBUM_TYPE.SHUTTERBUG, ALBUM_TYPE.EVENT];

    if (eventTypes.includes(album?.album_type || albumFolder?.album_type)) {
      return 'Back to event';
    }

    if (albumFolder) {
      return 'Back to my Gallery';
    }
    if (album) {
      return 'Back to my Album';
    }

    return `Back to Photo Manager`;
  })();

  const backBtnLink = (() => {
    if (!authenticated && albumFolder?.album_type === ALBUM_TYPE.EVENT) {
      return `/s/event-invite/${albumFolder.id}`;
    }
    if (albumFolder) {
      const basename = getBasenameFromEntity(albumFolder);
      return `/app/${basename}/folders/${albumFolder.id}`;
    }
    if (album) {
      const basename = getBasenameFromEntity(album);
      return `/app/${basename}/albums/${album.id}/photos`;
    }
    return '';
  })();

  return (
    <div className="wg-gallery-action-bar">
      <WebGalleryTitleBreadcrumb
        albumFolder={albumFolder}
        album={album}
        albumPhotoGroup={albumPhotoGroup}
        mobileSideNav={
          <WebGalleryActionDropdown
            authenticated={authenticated}
            album={album}
            albumFolder={albumFolder}
            blogUrl={blogUrl}
            videosUrl={videosUrl}
            favoritesUrl={showFavorites ? favoritesUrl : null}
            onFavoritesClick={onFavoritesClick}
            onInviteClick={() => {
              openModal(CONTRIBUTOR_SHARE_MODAL);
            }}
            videosTotal={videosTotal}
            blogPostsTotal={blogPostsTotal}
            unreadBlogPosts={unreadBlogPosts}
            backBtnLabel={backBtnLabel}
            backBtnLink={backBtnLink}
            isOwner={isOwner}
          />
        }
      />

      <div className="ml-lg-auto d-none d-lg-flex align-items-center">
        <FeatureBlock
          condition={canAccessMyMatchesFromWebGallery(
            albumFolder?.root || album,
          )}
        >
          <MyMatchesButton
            className="wg-my-matches-btn"
            albumMembershipGroupId={album?.id}
            albumFolderId={
              albumFolder?.root?.id ??
              album?.album_folder?.root?.id ??
              album?.root_album_folder?.id
            }
            btnStyle="reset"
            disableWithoutEntitlement={
              (albumFolder && !albumFolder.joins_enabled) ||
              (album && !album.root_album_folder?.joins_enabled)
            }
          >
            <div className="d-flex align-items-center">
              <img
                width="60px"
                src={getAsset('/waldo-magnified.svg')}
                alt="Waldo"
              />{' '}
              <span className="text-primary text-extra-bold">
                Looking for photos?
              </span>
            </div>
          </MyMatchesButton>
        </FeatureBlock>

        <div className="d-none d-lg-flex">
          {canAccessBack && backBtnLink && (
            <WebGalleryActionBarButton
              to={backBtnLink}
              icon={<HomeIcon />}
              label={backBtnLabel}
            />
          )}
          {authenticated && entity?.album_type === ALBUM_TYPE.SHUTTERBUG && (
            <WebGalleryActionBarButton
              onClick={() => {
                openModal(CONTRIBUTOR_SHARE_MODAL);
              }}
              icon={<InviteIcon />}
              label="Invite"
            />
          )}
          {(entity?.id !== albumFolder?.id ||
            canUploadToChildAlbumFolders(entity)) && (
            <ContributeButton>
              {({ onClick }) => (
                <WebGalleryActionBarButton
                  onClick={onClick}
                  icon={<UploadIcon />}
                  label="Upload"
                />
              )}
            </ContributeButton>
          )}
          {blogPostsTotal > 0 && blogUrl && (
            <WebGalleryActionBarButton
              to={blogUrl}
              icon={<BlogIcon />}
              label="Blog"
              count={
                unreadBlogPosts > 0 && authenticated
                  ? unreadBlogPosts
                  : undefined
              }
            />
          )}
          {videosTotal > 0 &&
            videosUrl &&
            !isVideoAlbumBool &&
            ![ALBUM_TYPE.EVENT, ALBUM_TYPE.SHUTTERBUG].includes(
              albumFolder?.album_type,
            ) &&
            !album &&
            !albumPhotoGroup && (
              <WebGalleryActionBarButton
                to={videosUrl}
                icon={<VideoIcon />}
                label="Videos"
              />
            )}
          {favoritesUrl && showFavorites && (
            <WebGalleryActionBarButton
              onClick={onFavoritesClick}
              icon={<FavoritesIcon />}
              label="Favorites"
            />
          )}
          {slideshowUrl && (
            <WebGalleryActionBarButton
              to={slideshowUrl}
              icon={<SlideshowIcon />}
              label="Slideshow"
            />
          )}
          <ContributorShare />

          {authenticated && (
            <>
              <ContactCircleSaveModal />
              <ContactCircleShareModal />
            </>
          )}
        </div>
      </div>
    </div>
  );
};

WebGalleryActionBar.propTypes = propTypes;

const withUser = graphql(MeQuery, {
  props: ({ data: { me } }) => ({
    user: me,
  }),
  skip: ({ authenticated }) => !authenticated,
});

const withAlbumFolderWithBlogPosts = graphql(AlbumFolderWithBlogPosts, {
  options: ({ match }) => ({
    variables: {
      id: match.params.folderId,
    },
  }),
  props: ({ data: { albumFolder } }) => ({
    blogPostsTotal: albumFolder?.linked_blog_posts?.total ?? 0,
    unreadBlogPosts: (albumFolder?.linked_blog_posts?.records ?? []).filter(
      blogPost => !blogPost.is_viewed,
    ).length,
  }),
  skip: ({ match }) => !match.params.folderId,
});

const withAlbumWithBlogPosts = graphql(AlbumWithBlogPosts, {
  options: ({ match }) => ({
    variables: {
      id: match.params.albumId,
    },
  }),
  props: ({ data: { album } }) => ({
    blogPostsTotal: album?.linked_blog_posts?.total ?? 0,
    unreadBlogPosts: (album?.linked_blog_posts?.records ?? []).filter(
      blogPost => !blogPost.is_viewed,
    ).length,
  }),
  skip: ({ match }) => !match.params.albumId || match.params.photoGroupId,
});

const withAlbumPhotoGroupWithBlogPosts = graphql(AlbumPhotoGroupWithBlogPosts, {
  options: ({ match }) => ({
    variables: {
      id: match.params.photoGroupId,
    },
  }),
  props: ({ data: { albumPhotoGroup } }) => ({
    albumPhotoGroup,
    blogPostsTotal: albumPhotoGroup?.linked_blog_posts?.total ?? 0,
    unreadBlogPosts: (albumPhotoGroup?.linked_blog_posts?.records ?? []).filter(
      blogPost => !blogPost.is_viewed,
    ).length,
  }),
  skip: ({ match }) => !match.params.photoGroupId,
});

const withAlbumFolderWithVideos = graphql(AlbumFolderWithVideos, {
  options: ({ match }) => ({
    variables: {
      id: match.params.folderId,
    },
  }),
  props: ({ data: { albumFolder } }) => ({
    videosTotal: albumFolder?.videos.total ?? 0,
  }),
  skip: ({ match }) => !match.params.folderId,
});

const withAlbumWithVideos = graphql(AlbumWithVideos, {
  options: ({ match }) => ({
    variables: {
      id: match.params.albumId,
    },
  }),
  props: ({ data: { album } }) => ({
    videosTotal: album?.videos.total ?? 0,
  }),
  skip: ({ match }) => !match.params.albumId || match.params.photoGroupId,
});

const withAlbumPhotoGroupWithVideos = graphql(AlbumPhotoGroupWithVideos, {
  options: ({ match }) => ({
    variables: {
      id: match.params.photoGroupId,
    },
  }),
  props: ({ data: { albumPhotoGroup } }) => ({
    videosTotal: albumPhotoGroup?.videos.total ?? 0,
  }),
  skip: ({ match }) => !match.params.photoGroupId,
});

const withAlbumWithSlideshow = graphql(AlbumSlideshowForModeCheck, {
  options: ({ match }) => ({
    variables: {
      id: match.params.albumId,
    },
  }),
  props: ({ data: { album } }) => ({
    slideshow: album?.slideshow,
  }),
  skip: ({ match }) => !match?.params?.albumId,
});

const withAlbumFavorites = graphql(AlbumFavoritePhotoIds, {
  options: ({ match }) => ({
    variables: {
      limit: 1,
      id: match.params.albumId,
    },
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'cache-and-network',
  }),
  props: ({ data: { album } }) => ({
    hasAlbumFavorites: (album?.account_favorite_photos?.total ?? 0) > 0,
  }),
  skip: ({ match, authenticated }) => !authenticated || !match?.params?.albumId,
});
const withAlbumFolderFavorites = graphql(AlbumFolderFavoritePhotoIds, {
  options: ({ match }) => ({
    variables: {
      limit: 1,
      id: match.params.folderId,
    },
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'cache-and-network',
  }),
  props: ({ data: { albumFolder } }) => ({
    hasAlbumFolderFavorites:
      (albumFolder?.account_favorite_photos?.total ?? 0) > 0,
  }),
  skip: ({ match, authenticated }) =>
    !authenticated || !match?.params?.folderId,
});

const mapStateToProps = ({ auth }) => ({
  authenticated: auth.authenticated,
});

const mapDispatchToProps = dispatch => ({
  login: actionProps =>
    dispatch(openModalAction(PHONE_AUTH_MODAL, actionProps)),
  openModal: (...args) => dispatch(openModalAction(...args)),
});

export { WebGalleryActionBar as WebGalleryActionBarComponent };

export default compose(
  withRouter,
  connect(mapStateToProps, mapDispatchToProps),
  withUser,
  withAlbumFolderWithBlogPosts,
  withAlbumWithBlogPosts,
  withAlbumPhotoGroupWithBlogPosts,
  withAlbumFolderWithVideos,
  withAlbumWithVideos,
  withAlbumPhotoGroupWithVideos,
  withAlbumWithSlideshow,
  withAlbumFavorites,
  withAlbumFolderFavorites,
)(WebGalleryActionBar);
